import React, { useState, useRef, useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { NoteManager } from 'components/Notes/NoteManager'
import { Dropdown, IDropdownItem } from 'components/UI/Dropdown/Dropdown'
import { cn } from 'components/UI/cn'
import { PiToolbox } from 'react-icons/pi'
import useApiService from 'services/api'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'
import { useCompanyOrDoorData } from 'hooks/useCompanyOrDoorData'
import {
  Handshake,
  NotebookPen,
  UserPlus,
  Tags,
  Combine,
  Trash,
  FileUp,
} from 'lucide-react'
import {
  PiCalendarStar,
  PiShippingContainer,
  PiHandshake,
  PiIdentificationCard,
  PiNotepad,
  PiEnvelope,
  PiFileText,
} from 'react-icons/pi'
import { BsTruck } from 'react-icons/bs'
import { LiaHandHoldingUsdSolid } from 'react-icons/lia'
import to from 'await-to-js'

// Import modals from the existing CompanyActionsComponent pattern
import CreateDealModal from 'components/Deals/CreateDealModal/CreateDealModal'
import NotesModal from 'components/Modals/NotesModal'
import NewContactModal from 'components/Modals/NewContactModal'
import SuggestionModal from 'components/Modals/SuggestionModal/SuggestionModal'
import { SingleChainMergeModal } from 'features/unmatched-companies/SingleChainMergeModal'
import ReminderModal from 'components/Reminders/ReminderModal'
import {
  SampleRequestFormModal,
  InitialDoorData,
} from 'components/Samples/SampleRequestFormModal'
import {
  SingleItemTagAssignModal,
  SingleItemDistributorAssignModal,
  SingleItemGPOAssignModal,
} from 'components/Actions/SingleItemAssignmentModals'
import { TableTypeSlug, CompanyTypeSlug } from 'models/companies'
import dialogConfirm from 'components/dialogConfirm'
import { UploadDocumentModal } from 'components/DocumentLibrary/UploadDocumentModal'

// Import section modals
import { ContactsModal } from './Modals/ContactsModal'
import { DealsModal } from './Modals/DealsModal'
import { NotesListModal } from './Modals/NotesListModal'
import { EmailsModal } from './Modals/EmailsModal/EmailsModal'
import { DocumentsModal } from './Modals/DocumentsModal'
import { useNavigate, useSearchParams } from 'react-router-dom'

const quickNoteSchema = z.object({
  note: z.string().optional().nullable(),
  attachments: z.any().optional().nullable(),
})

type QuickNoteInput = z.infer<typeof quickNoteSchema>

interface CompanyTopCardProps {
  chainProxyId?: number
  doorProxyId?: number
  chainID?: number
  contactCompanyID?: number
  companyName?: string
  websiteAddress?: string
  address?: {
    addressInfo?: {
      fullAddress?: string
    }
  }
  phone?: string
  companyTypeSlug: CompanyTypeSlug
  tableTypeSlug: TableTypeSlug
  isUserCreated?: boolean
  isDoorPage?: boolean
  /** Pre-loaded door data for sample requests - avoids redundant API calls */
  initialDoorData?: InitialDoorData
}

type ActionButtonDisplayMode = 'full' | 'icon-count' | 'icon-only'

interface ActionButtonProps {
  label: string
  count: number
  icon: React.ReactNode
  onClick?: () => void
  displayMode?: ActionButtonDisplayMode
}

const ActionButton: React.FC<ActionButtonProps> = ({
  label,
  count,
  icon,
  onClick,
  displayMode = 'full',
}) => (
  <button
    onClick={onClick}
    className="flex items-center justify-center gap-2 px-3 rounded-md border border-brand-hover bg-surface-hover h-[32px] hover:bg-surface-hover-deep transition-colors"
    title={displayMode !== 'full' ? `${label} (${count})` : undefined}
  >
    {displayMode !== 'full' && <span className="text-brand">{icon}</span>}
    {displayMode === 'full' && (
      <span className="font-medium text-sm leading-6 text-brand">{label}</span>
    )}
    {displayMode !== 'icon-only' && (
      <span className="font-light text-sm leading-[17px] text-center text-brand">
        ({count})
      </span>
    )}
  </button>
)

// Breakpoints for responsive actions row (adjusted for 5 buttons instead of 4)
const HIDE_ACTIONS_TEXT_BELOW = 650 // Below this: hide "Actions" text, show icon only
const SHOW_ICONS_WITH_COUNTS_BELOW = 590 // Below this: show icons + counts (no labels)
const SHOW_ICONS_ONLY_BELOW = 465 // Below this: show icons only (no counts)

export function CompanyTopCard({
  chainProxyId,
  doorProxyId,
  chainID,
  companyName,
  websiteAddress,
  address,
  phone,
  companyTypeSlug,
  tableTypeSlug,
  isUserCreated = false,
  isDoorPage = false,
  initialDoorData,
}: CompanyTopCardProps) {
  const api = useApiService()
  const queryClient = useQueryClient()
  const featureFlag = useFeatureFlagService()
  const navigate = useNavigate()
  // Responsive state for actions row
  const actionsRowRef = useRef<HTMLDivElement>(null)
  const [actionsRowWidth, setActionsRowWidth] = useState<number | null>(null)

  // Observe actions row width
  useEffect(() => {
    if (!actionsRowRef.current) return

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setActionsRowWidth(entry.contentRect.width)
      }
    })

    resizeObserver.observe(actionsRowRef.current)
    return () => resizeObserver.disconnect()
  }, [])

  // Derive display mode from width (default to 'full' before measurement)
  const actionButtonDisplayMode = useMemo((): ActionButtonDisplayMode => {
    if (
      actionsRowWidth === null ||
      actionsRowWidth > SHOW_ICONS_WITH_COUNTS_BELOW
    )
      return 'full'
    if (actionsRowWidth > SHOW_ICONS_ONLY_BELOW) return 'icon-count'
    return 'icon-only'
  }, [actionsRowWidth])

  const showActionsText = useMemo(
    () => actionsRowWidth === null || actionsRowWidth > HIDE_ACTIONS_TEXT_BELOW,
    [actionsRowWidth]
  )

  // Get the primary ID for operations
  const primaryId = chainProxyId || doorProxyId

  // Fetch company or door proxy data based on context
  const { data: effectiveData } = useCompanyOrDoorData(
    chainProxyId || 0,
    doorProxyId
  )

  // Modal states for action buttons
  // Deep-link target for the EmailsModal. When `?email_thread_id=<n>` is in
  // the URL, the modal auto-opens scrolled to that thread (FIR-7104). Stripped
  // on manual close so refreshes don't re-open the modal after the user closed
  // it; kept on initial open so the URL stays shareable / refreshable.
  const [searchParams, setSearchParams] = useSearchParams()
  const initialEmailThreadId = useMemo(() => {
    const raw = searchParams.get('email_thread_id')
    const n = raw ? Number(raw) : NaN
    return Number.isFinite(n) && n > 0 ? n : undefined
  }, [searchParams])

  const [showContactsModal, setShowContactsModal] = useState(false)
  const [showDealsModal, setShowDealsModal] = useState(false)
  const [showNotesModal, setShowNotesModal] = useState(false)
  const [showEmailsModal, setShowEmailsModal] = useState(!!initialEmailThreadId)
  const [showDocumentsModal, setShowDocumentsModal] = useState(false)

  // Modal states for dropdown actions
  const [showCreateDeal, setShowCreateDeal] = useState(false)
  const [showCreateNote, setShowCreateNote] = useState(false)
  const [showAddContact, setShowAddContact] = useState(false)
  const [showManageTags, setShowManageTags] = useState(false)
  const [showAddDistributor, setShowAddDistributor] = useState(false)
  const [showAddGPO, setShowAddGPO] = useState(false)
  const [showMergeCompany, setShowMergeCompany] = useState(false)
  const [showReminderModal, setShowReminderModal] = useState(false)
  const [showSampleRequestModal, setShowSampleRequestModal] = useState(false)
  const [showUploadDocument, setShowUploadDocument] = useState(false)

  // Quick note form
  const { control, handleSubmit, reset, formState } = useForm<QuickNoteInput>({
    defaultValues: {
      note: '',
      attachments: [],
    },
    resolver: zodResolver(quickNoteSchema),
  })

  // Watch the note field to determine if Save button should be enabled
  const noteText = useWatch({ control, name: 'note' })

  // Actions dropdown configuration (same as CompanyActionsComponent)
  const dropdownItems: IDropdownItem[] = [
    {
      label: 'Create a Deal',
      icon: <Handshake size={16} />,
      callback: () => setShowCreateDeal(true),
    },
    {
      label: 'Create a Note',
      icon: <NotebookPen size={16} />,
      callback: () => setShowCreateNote(true),
    },
    {
      label: 'Upload Document',
      icon: <FileUp size={16} />,
      callback: () => setShowUploadDocument(true),
    },
    {
      label: 'Add a Contact',
      icon: <UserPlus size={16} />,
      callback: () => setShowAddContact(true),
    },
    {
      label: 'Manage Tags',
      icon: <Tags size={16} />,
      callback: () => setShowManageTags(true),
    },
    {
      label: 'Add Distributor',
      icon: <BsTruck size={16} />,
      callback: () => setShowAddDistributor(true),
    },
    {
      label: 'Add GPO',
      icon: <PiShippingContainer size={16} />,
      callback: () => setShowAddGPO(true),
    },
    ...(featureFlag.enableSamples
      ? [
          {
            label: 'Sample Request',
            icon: <LiaHandHoldingUsdSolid size={16} />,
            callback: () => setShowSampleRequestModal(true),
          },
        ]
      : []),
    ...(featureFlag.enableReminders
      ? [
          {
            label: 'Schedule Reminder',
            icon: <PiCalendarStar size={16} />,
            callback: () => setShowReminderModal(true),
          },
        ]
      : []),
    // Conditionally add actions based on whether it's user-created
    ...(!doorProxyId && isUserCreated
      ? [
          {
            label: 'Merge Company',
            icon: <Combine size={16} />,
            callback: () => setShowMergeCompany(true),
          },
        ]
      : []),
    ...(isUserCreated
      ? [
          {
            label: isDoorPage ? 'Delete Door' : 'Delete Company',
            icon: <Trash size={16} />,
            callback: async () => {
              const entityLabel = isDoorPage ? 'Door' : 'Company'
              if (
                await dialogConfirm(
                  isDoorPage
                    ? 'Are you sure you want to delete this door? This action cannot be undone.'
                    : 'Deleting this company will also delete any notes, tags, deals, or contacts you may have added. This action cannot be undone.',
                  `Delete ${entityLabel}`,
                  undefined,
                  {
                    buttonVariant: 'danger',
                    confirmLabel: `Delete ${entityLabel}`,
                  }
                )
              ) {
                if (isDoorPage) {
                  if (!doorProxyId) return
                  const [err] = await to(api.deleteDoorProxy(doorProxyId))
                  if (err) {
                    toast.error('Error deleting door')
                    return
                  }
                  toast.success('Door deleted')
                } else {
                  if (!effectiveData?.id) return
                  const [err] = await to(api.deleteCompany(effectiveData.id!))
                  if (err) {
                    toast.error('Error deleting company')
                    return
                  }
                  toast.success('Company deleted')
                }
                navigate('/')
              }
            },
          },
        ]
      : []),
  ]

  // Handle quick note save
  const [isSavingNote, setIsSavingNote] = useState(false)
  const onSubmitQuickNote = handleSubmit(async (values) => {
    // Require text content - attachments alone are not sufficient for a note
    if (values.note?.trim()) {
      setIsSavingNote(true)
      try {
        await api.createNote(
          chainID || chainProxyId,
          chainProxyId,
          values.note || '',
          values.attachments || [],
          doorProxyId
        )
        toast.success('Note saved successfully')
        reset({
          note: '',
          attachments: [],
        })
        handleRefreshData()
      } catch (error) {
        toast.error('Failed to save note')
      } finally {
        setIsSavingNote(false)
      }
    }
  })

  // Refresh data after actions (same logic as CompanyActionsComponent)
  const handleRefreshData = () => {
    // Specifically refetch the company or door proxy query to refresh counts immediately
    if (chainProxyId) {
      queryClient.refetchQueries({
        queryKey: ['company', chainProxyId],
      })
    }
    if (doorProxyId) {
      queryClient.refetchQueries({
        queryKey: ['door_proxy', doorProxyId],
      })
    }

    // Remove cached queries for deals, contacts, notes, and emails modals to force fresh data
    // This ensures that when modals reopen, they fetch fresh data instead of using stale cache
    if (chainProxyId) {
      queryClient.removeQueries({
        predicate: (query) => {
          const key = query.queryKey
          if (!Array.isArray(key) || key.length === 0) return false

          const tableKey = key[0]
          if (typeof tableKey !== 'string') return false

          // Remove queries for the modal tables
          return (
            tableKey.includes('deals-modal-company') ||
            tableKey.includes('contacts-modal-company') ||
            tableKey.includes('notes-modal-company') ||
            tableKey.includes('emails-modal-company') ||
            tableKey.includes('documents-modal-company')
          )
        },
      })
    }

    if (doorProxyId) {
      queryClient.removeQueries({
        predicate: (query) => {
          const key = query.queryKey
          if (!Array.isArray(key) || key.length === 0) return false

          const tableKey = key[0]
          if (typeof tableKey !== 'string') return false

          // Remove queries for the modal tables
          return (
            tableKey.includes('deals-modal-door') ||
            tableKey.includes('contacts-modal-door') ||
            tableKey.includes('notes-modal-door') ||
            tableKey.includes('emails-modal-door') ||
            tableKey.includes('documents-modal-door')
          )
        },
      })
    }

    // Invalidate activity tables and company activity to show document actions in activity feed
    queryClient.invalidateQueries({
      predicate: (query) => {
        const firstKey = query.queryKey[0]
        return (
          typeof firstKey === 'string' &&
          (firstKey.includes('activity-table') ||
            firstKey === 'companyActivity')
        )
      },
    })

    // Also invalidate all other company-related queries (for general updates)
    queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey
        if (!Array.isArray(key)) return false

        const shouldInvalidate = key.some((k) => {
          if (typeof k === 'number') {
            return k === primaryId || k === chainProxyId || k === doorProxyId
          }
          if (typeof k === 'string') {
            return (
              k.includes('contactCompany') ||
              k.includes('doorProxy') ||
              k.includes('company') ||
              k.includes('chain') ||
              (chainProxyId && k.includes(`${chainProxyId}`)) ||
              (doorProxyId && k.includes(`${doorProxyId}`)) ||
              (primaryId && k.includes(`${primaryId}`))
            )
          }
          return false
        })

        return shouldInvalidate
      },
    })
  }

  // Create fallback contact company object with real data when available
  const effectiveContactCompany: import('models/companies').ContactCompany = {
    id: chainProxyId || 0,
    name: effectiveData?.name || companyName || '',
    domain: effectiveData?.domain || websiteAddress || '',
    note_count: effectiveData?.note_count || 0,
    deal_count: effectiveData?.deal_count || 0,
    contact_count: effectiveData?.contact_count || 0,
  }

  return (
    <>
      <div
        ref={(el) => {
          if (el) {
            document.documentElement.style.setProperty(
              '--company-top-card-height',
              `${el.offsetHeight}px`
            )
          }
        }}
        className="bg-white rounded-lg border border-line overflow-x-auto py-4 shadow-md sticky top-0 z-[4]"
      >
        {/* Content with horizontal padding */}
        <div className="space-y-5">
          {/* Top Actions Row */}
          <div
            ref={actionsRowRef}
            className="flex items-center justify-between px-4 h-11"
          >
            {/* Left side - Action buttons */}
            <div className="flex items-center gap-2.5">
              <ActionButton
                label="Contacts"
                count={effectiveData?.contact_count ?? 0}
                icon={<PiIdentificationCard size={22} />}
                onClick={() => setShowContactsModal(true)}
                displayMode={actionButtonDisplayMode}
              />
              <ActionButton
                label="Deals"
                count={effectiveData?.deal_count ?? 0}
                icon={<PiHandshake size={22} />}
                onClick={() => setShowDealsModal(true)}
                displayMode={actionButtonDisplayMode}
              />
              <ActionButton
                label="Notes"
                count={effectiveData?.note_count ?? 0}
                icon={<PiNotepad size={22} />}
                onClick={() => setShowNotesModal(true)}
                displayMode={actionButtonDisplayMode}
              />
              <ActionButton
                label="Emails"
                count={effectiveData?.email_count ?? 0}
                icon={<PiEnvelope size={22} />}
                onClick={() => setShowEmailsModal(true)}
                displayMode={actionButtonDisplayMode}
              />
              <ActionButton
                label="Documents"
                count={effectiveData?.document_count ?? 0}
                icon={<PiFileText size={22} />}
                onClick={() => setShowDocumentsModal(true)}
                displayMode={actionButtonDisplayMode}
              />
            </div>

            {/* Right side - Actions dropdown */}
            <div className="flex items-center justify-end">
              <Dropdown
                className="focus:outline-none focus:ring-0"
                buttonIcon={<PiToolbox size={24} />}
                buttonLabel={showActionsText ? 'Actions' : undefined}
                buttonVariant="primary"
                items={dropdownItems}
              />
            </div>
          </div>

          {/* Quick Note Editor */}
          <div className="px-4">
            <NoteManager
              control={control}
              noteName="note"
              attachmentsName="attachments"
              placeholder="Start writing note here"
              enableMentions={true}
              editorClassName="h-36 bg-surface-alt"
              toolbarClassName="bg-surface-alt"
              label=""
              disabled={isSavingNote}
              toolbarSuffix={
                formState.isDirty ? (
                  <>
                    <button
                      type="button"
                      onClick={() => reset({ note: '', attachments: [] })}
                      className="text-sm text-fg-secondary hover:text-fg transition-colors px-2 py-1"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={onSubmitQuickNote}
                      disabled={!noteText?.trim() || isSavingNote}
                      className={cn(
                        'text-sm font-medium transition-colors px-2 py-1',
                        noteText?.trim()
                          ? 'text-brand hover:text-brand-hover'
                          : 'text-fg-secondary cursor-not-allowed'
                      )}
                    >
                      {isSavingNote ? 'Saving...' : 'Save'}
                    </button>
                  </>
                ) : undefined
              }
            />
          </div>
        </div>
      </div>

      {/* All Modals (same as CompanyActionsComponent) */}

      {/* Create Deal Modal */}
      {showCreateDeal && (
        <CreateDealModal
          show={showCreateDeal}
          handleClose={() => setShowCreateDeal(false)}
          targetCompanyId={chainProxyId}
          targetDoorProxyId={doorProxyId}
          forDoorSelection={!!doorProxyId}
          tableTypeSlug={tableTypeSlug}
          companyTypeSlug={companyTypeSlug}
          totalSelectedRows={1}
          optimisticUiTarget="deals-section"
          onDealCreated={handleRefreshData}
        />
      )}

      {/* Create Note Modal */}
      {showCreateNote && (
        <NotesModal
          show={showCreateNote}
          handleClose={() => setShowCreateNote(false)}
          chainId={chainID}
          contactCompanyId={chainProxyId}
          doorProxyId={doorProxyId}
          onSubmitSuccess={handleRefreshData}
        />
      )}

      {/* Add Contact Modal */}
      {showAddContact && (
        <NewContactModal
          show={showAddContact}
          handleClose={() => setShowAddContact(false)}
          onContactCreated={handleRefreshData}
          forCompany={effectiveContactCompany}
          initialDoorIds={doorProxyId ? [doorProxyId] : undefined}
        />
      )}

      {/* Upload Document Modal */}
      {showUploadDocument && (
        <UploadDocumentModal
          isOpen={showUploadDocument}
          onClose={() => setShowUploadDocument(false)}
          preSelectedCompanyId={chainProxyId}
          preSelectedCompanyName={effectiveData?.name || companyName}
          onSuccess={handleRefreshData}
        />
      )}

      {/* Manage Tags Modal */}
      {showManageTags && primaryId && (
        <SingleItemTagAssignModal
          show={showManageTags}
          handleClose={() => setShowManageTags(false)}
          contactCompanyId={chainProxyId}
          doorProxyId={doorProxyId}
          contactCompany={effectiveContactCompany}
          onSuccess={handleRefreshData}
          isDoorPage={isDoorPage}
        />
      )}

      {/* Add Distributor Modal */}
      {showAddDistributor && primaryId && (
        <SingleItemDistributorAssignModal
          show={showAddDistributor}
          handleClose={() => setShowAddDistributor(false)}
          contactCompanyId={chainProxyId}
          doorProxyId={doorProxyId}
          contactCompany={effectiveContactCompany}
          onSuccess={handleRefreshData}
          isDoorPage={isDoorPage}
        />
      )}

      {/* Add GPO Modal */}
      {showAddGPO && primaryId && (
        <SingleItemGPOAssignModal
          show={showAddGPO}
          handleClose={() => setShowAddGPO(false)}
          contactCompanyId={chainProxyId}
          doorProxyId={doorProxyId}
          contactCompany={effectiveContactCompany}
          onSuccess={handleRefreshData}
          isDoorPage={isDoorPage}
        />
      )}

      {/* Schedule Reminder Modal */}
      {featureFlag.enableReminders && showReminderModal && (
        <ReminderModal
          open={showReminderModal}
          onClose={() => setShowReminderModal(false)}
          defaultCompanyId={chainProxyId}
          defaultDoorProxyId={doorProxyId}
          onCreated={() => {
            setShowReminderModal(false)
            handleRefreshData()
          }}
        />
      )}

      {/* Sample Request Modal */}
      {featureFlag.enableSamples && showSampleRequestModal && (
        <SampleRequestFormModal
          show={showSampleRequestModal}
          onHide={() => {
            setShowSampleRequestModal(false)
            handleRefreshData()
          }}
          initialCompanyId={chainProxyId}
          initialDoorProxyId={doorProxyId}
          initialDoorData={initialDoorData}
        />
      )}

      {/* Merge Company Modal (for user-created companies) */}
      {isUserCreated && chainProxyId && (
        <SingleChainMergeModal
          show={showMergeCompany}
          onClose={() => setShowMergeCompany(false)}
          contactCompanyId={chainProxyId}
          contactCompanyName={companyName ?? ''}
        />
      )}

      {/* Hidden SuggestionModal that we'll trigger programmatically */}
      {!isUserCreated && (
        <div style={{ display: 'none' }}>
          <SuggestionModal
            contactCompanyId={chainProxyId!}
            tableTypeSlug={tableTypeSlug}
            doorId={doorProxyId}
            defaultValues={{
              suggested_name: companyName,
              suggested_website: websiteAddress ?? '',
              ...(doorProxyId && {
                suggested_address: address?.addressInfo?.fullAddress,
                suggested_phone_number: phone,
              }),
            }}
          />
        </div>
      )}

      {/* Contacts Modal */}
      {showContactsModal && effectiveContactCompany && (
        <ContactsModal
          show={showContactsModal}
          onHide={() => setShowContactsModal(false)}
          contactCompany={effectiveContactCompany}
          forDoorProxyId={doorProxyId}
          companyName={companyName}
        />
      )}

      {/* Deals Modal */}
      {showDealsModal && effectiveContactCompany && (
        <DealsModal
          show={showDealsModal}
          onHide={() => setShowDealsModal(false)}
          contactCompany={effectiveContactCompany}
          forDoorProxyId={doorProxyId}
          companyName={companyName}
        />
      )}

      {/* Notes List Modal */}
      {showNotesModal && (
        <NotesListModal
          show={showNotesModal}
          onHide={() => setShowNotesModal(false)}
          chainId={chainID}
          contactCompanyId={chainProxyId}
          doorProxyId={doorProxyId}
          companyName={companyName}
        />
      )}

      {/* Emails Modal */}
      {showEmailsModal && effectiveContactCompany && (
        <EmailsModal
          show={showEmailsModal}
          onHide={() => {
            setShowEmailsModal(false)
            // Strip the deep-link param on close so a later refresh doesn't
            // re-open the modal. No-op when the param wasn't there to begin
            // with (manual open from the action bar).
            if (searchParams.has('email_thread_id')) {
              setSearchParams(
                (prev) => {
                  const next = new URLSearchParams(prev)
                  next.delete('email_thread_id')
                  return next
                },
                { replace: true }
              )
            }
          }}
          contactCompany={effectiveContactCompany}
          forDoorProxyId={doorProxyId}
          companyName={companyName}
          initialThreadId={initialEmailThreadId}
        />
      )}

      {/* Documents Modal */}
      {showDocumentsModal && effectiveContactCompany && (
        <DocumentsModal
          show={showDocumentsModal}
          onHide={() => setShowDocumentsModal(false)}
          contactCompany={effectiveContactCompany}
          forDoorProxyId={doorProxyId}
          companyName={companyName}
        />
      )}
    </>
  )
}
